import dayjs, { type Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

// The canonical way to work with store-local time in this codebase — both client and server
// code should reach for these instead of calling `dayjs()`/`dayjs.tz()` directly. Two mistakes
// this prevents:
//   1. Forgetting `.tz(storeTimezone)` on "now", which silently uses the browser's/server's own
//      timezone instead of the store's — wrong whenever the viewer isn't in the store's timezone.
//   2. Re-exporting a raw, plugin-extended `dayjs` for callers to chain `.tz()` on themselves,
//      which is what apps/web/utils/dayjsTz.ts does — safe on the client, but known to silently
//      lose the timezone plugin in Nitro's production ESM bundle when a *server* file imports it
//      and calls `.tz()` in its own module scope (see CLAUDE.md Known Gotchas). Every function
//      here does its `.tz()` work internally, in the same module where the plugins were
//      extended, so it's safe to import from server-fns/ too.
//
// Booking start/end times are stored as naive ISO8601 strings (e.g. "2024-06-14T10:00:00") with
// no timezone info — they represent the store's own local wall clock, per Store.timezone.

/** "Now", viewed from the store's timezone — not the browser's or server's own local time. */
export const storeNow = (storeTimezone: string): Dayjs => dayjs().tz(storeTimezone);

/** The store's current calendar date (YYYY-MM-DD), per its own timezone. */
export const storeToday = (storeTimezone: string): string =>
    storeNow(storeTimezone).format("YYYY-MM-DD");

// --- Viewer-timezone-independent instant math ---------------------------------------------
// dayjs's `tz()` plugin models a fixed-offset time by shifting the underlying Date by
// `offset + viewerLocalOffset(at the true instant)` and reading it back with
// `viewerLocalOffset(at the shifted date)`. When the *viewer's* (browser's/server's) timezone
// changes DST between those two instants the difference leaks out — both `dayjs.tz(naive, tz)`
// and `dayjs(instant).tz(tz)` can be an hour off (e.g. TZ=Europe/London parsing a Melbourne
// time on 2026-03-29, or TZ=America/Los_Angeles on 2026-03-08). So the instant is computed here
// from Intl.DateTimeFormat, which only ever looks at the *store's* zone, and is then held in
// UTC mode, whose offset is always 0 and therefore has no viewer round-trip to leak.

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

// Cached: parseStoreTime() sits in per-cell/per-booking loops, and building an
// Intl.DateTimeFormat is far costlier than calling formatToParts on one.
const zoneFormatter = (storeTimezone: string): Intl.DateTimeFormat => {
    let formatter = zoneFormatters.get(storeTimezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-US", {
            timeZone: storeTimezone,
            hour12: false,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        zoneFormatters.set(storeTimezone, formatter);
    }
    return formatter;
};

// UTC offset (ms) of `storeTimezone` at a given instant, read straight from Intl rather than via
// dayjs's timezone plugin — see `formatStoreTime` for why the plugin can't be trusted here.
const zoneOffsetAt = (instantMs: number, storeTimezone: string): number => {
    // Intl has no sub-second field, so compare against the instant's whole second.
    const wholeSecondMs = Math.floor(instantMs / 1000) * 1000;
    const parts: Record<string, string> = {};
    for (const { type, value } of zoneFormatter(storeTimezone).formatToParts(
        new Date(wholeSecondMs),
    )) {
        parts[type] = value;
    }
    // Some engines render midnight as hour "24" (the h24 hour cycle) instead of "00". The date
    // parts still name the day that midnight *begins*, so only the hour needs normalising —
    // advancing the date as well would put the offset out by a full 24 hours. Pinned by a test.
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return (
        Date.parse(
            `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}Z`,
        ) - wholeSecondMs
    );
};

const ONE_DAY_MS = 86_400_000;

// The real instant at which `storeTimezone`'s wall clock reads `naiveDateTime`.
// The store's offset can change within a day of any wall time, so the two candidate offsets are
// sampled a day either side and each is kept only if it is actually in force at the instant it
// produces. A wall time in a fall-back overlap has two valid instants — the earlier one is
// returned; one in a spring-forward gap has none — it is read with the pre-transition offset,
// i.e. lands just after the gap (the same "compatible" rule as Temporal). `dayjs.tz()` resolved
// these times from the offset in force *today*, so its answer for them depended on the date the
// code ran; this one is deterministic.
const storeWallClockToInstant = (naiveDateTime: string, storeTimezone: string): number => {
    const asIfUtc = dayjs.utc(naiveDateTime).valueOf();
    const offsetBefore = zoneOffsetAt(asIfUtc - ONE_DAY_MS, storeTimezone);
    const offsetAfter = zoneOffsetAt(asIfUtc + ONE_DAY_MS, storeTimezone);
    const validOffsets = [offsetBefore, offsetAfter]
        .filter((offset) => zoneOffsetAt(asIfUtc - offset, storeTimezone) === offset)
        .sort((x, y) => y - x); // larger offset ⇒ earlier instant
    return asIfUtc - (validOffsets[0] ?? offsetBefore);
};

/**
 * Parses a naive, timezone-less datetime string (e.g. a booking's startTime/endTime) as the
 * store's local wall clock, returning the real instant it denotes, for comparing against
 * `storeNow()` or another `parseStoreTime()` result, or for `.add()`/`.subtract()`/`.diff()`.
 *
 * The result is a **UTC-mode** Dayjs: its instant is correct in every viewer timezone, but
 * `.format()` / `.startOf("day")` on it render/cut in UTC, NOT the store's wall clock. To display
 * it use `formatStoreTime()`; for the store's midnight use `storeStartOfDay()`.
 */
export const parseStoreTime = (naiveDateTime: string, storeTimezone: string): Dayjs => {
    if (Number.isNaN(dayjs.utc(naiveDateTime).valueOf())) return dayjs.utc(Number.NaN);
    return dayjs.utc(storeWallClockToInstant(naiveDateTime, storeTimezone));
};

/**
 * Same instant as `parseStoreTime()` — which is already UTC-mode — kept as the explicit name for
 * building iCalendar / calendar deep-link timestamps, which must be expressed in UTC.
 */
export const parseStoreTimeToUtc = (naiveDateTime: string, storeTimezone: string): Dayjs =>
    parseStoreTime(naiveDateTime, storeTimezone);

/**
 * The instant at which the store's calendar day containing `instant` begins (its local 00:00),
 * as a UTC-mode Dayjs. `.startOf("day")` on a `parseStoreTime()` result would cut at UTC midnight.
 */
export const storeStartOfDay = (instant: Dayjs | Date | number, storeTimezone: string): Dayjs => {
    const instantMs = dayjs(instant).valueOf();
    const storeDate = dayjs
        .utc(instantMs + zoneOffsetAt(instantMs, storeTimezone))
        .format("YYYY-MM-DD");
    return parseStoreTime(`${storeDate} 00:00`, storeTimezone);
};

/**
 * Formats a real instant (e.g. an ISO string from a `Timestamptz` column such as
 * `updatedAt`, which already carries its own UTC offset — unlike a naive
 * startTime/endTime) as a calendar date/time in the store's timezone. Display only —
 * for comparisons, work with `storeNow()`/`parseStoreTime()` instead.
 */
export const formatInstantInStoreTimezone = (
    instant: string,
    storeTimezone: string,
    format: string,
): string => {
    const instantMs = dayjs(instant).valueOf();
    // Invalid input: keep dayjs's "Invalid Date" rather than letting Intl throw a RangeError.
    if (Number.isNaN(instantMs)) return dayjs(instant).format(format);
    // Not `dayjs(instant).tz(tz).format()`: that path leaks the viewer's DST (see the note above
    // parseStoreTime). Shift by the store's Intl-derived offset and format in UTC mode instead;
    // don't use `Z`/`ZZ` tokens — they would print +00:00.
    return dayjs.utc(instantMs + zoneOffsetAt(instantMs, storeTimezone)).format(format);
};

/**
 * Formats a naive store-local datetime string (optionally shifted by `plusMinutes`) for display,
 * in the store's own timezone.
 *
 * Use this instead of `parseStoreTime(...).format(...)`. dayjs represents a zoned time as a fixed
 * UTC offset by shifting the underlying JS `Date` by `offset + viewerLocalOffset`, then reading
 * the wall clock back through the *viewer's* local zone. Whenever the viewer's timezone has a DST
 * transition between the real instant and that shifted date, the two local offsets disagree and
 * the difference leaks straight into the output — e.g. a 19:00 Melbourne booking renders as
 * "6:00 PM" for a Los Angeles viewer on 2026-11-01 (US DST ends). `Intl.DateTimeFormat` resolves
 * the wall clock in `storeTimezone` directly, so it cannot drift.
 *
 * `parseStoreTime()` resolves its instant through the same Intl helpers, so comparing/adding its
 * results is correct in every viewer timezone — but because that result is UTC-mode, `.format()`
 * on it renders UTC rather than the store's wall clock. This is the way to display it.
 */
export const formatStoreTime = (
    naiveDateTime: string,
    storeTimezone: string,
    options: Intl.DateTimeFormatOptions,
    plusMinutes = 0,
): string =>
    new Intl.DateTimeFormat("en-US", { ...options, timeZone: storeTimezone })
        .format(
            new Date(
                storeWallClockToInstant(naiveDateTime, storeTimezone) + plusMinutes * 60 * 1000,
            ),
        )
        // Newer ICU builds separate the time from AM/PM with U+202F rather than a plain space.
        .replace(/\u202f/g, " ");
